import React, { useCallback, useState } from 'react';

import {
  Edit,
  TextField,
  DateField,
  usePermissions,
  useNotify,
  Labeled,
  TabbedShowLayout,
  ReferenceManyField,
  Datagrid,
  Pagination,
  SelectField,
  useRecordContext,
  useDataProvider,
  useGetRecordId,
  useRefresh,
  TopToolbar,
  Confirm,
  TabbedShowLayoutTabs,
  TabbedShowLayoutTabsProps,
  BooleanField,
  BooleanInput,
  Form,
  SaveButton,
} from 'react-admin';
import { FieldValues } from 'react-hook-form';

import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Grid,
  Stack,
} from '@mui/material';
import CreateIcon from '@mui/icons-material/Create';

import hasAccess from '../../utilities/hasAccess';
import errorNotification from '../../utilities/errorNotification';
import useHiddenUntilHover from '../../hooks/useHiddenUntilHover';

import actions, { Actions } from '../../constants/actions';
import resourceNames from '../../constants/resourceNames';
import resourceTypes from '../../constants/resourceTypes';
import { boldDataGridStyle } from '../../constants/style/datagridStyles';
import { IForm } from '../../utilities/schemas/form';

import EditArrayInput from '../../customFields/EditArrayInput';
import EditField from '../../customFields/EditField';
import EditImageField from '../../customFields/EditImageField';
import EditReferenceField from '../../customFields/EditReferenceField';
import ReferenceMemberField from '../../customFields/ReferenceMemberField';
import FormBreadcrumb from './FormBreadcrumb';
import TestLinkButtons from './TestLinkButtons';
import { FormActivityChip, FormStateChip } from './FormStatusChips';
import { languagesChoices } from '../../constants/languages';

const FormEditActions = () => {
  const record = useRecordContext<IForm>();

  if (!record) return null;

  return (
    <TopToolbar sx={{ alignItems: 'center', justifyContent: 'flex-start' }}>
      <Stack direction="row" spacing={1} alignItems="center">
        <FormStateChip state={record.state} />
        <FormActivityChip inactive={record.inactive} archived={record.archived} />
      </Stack>
    </TopToolbar>
  );
};

const FormTabs = (props: TabbedShowLayoutTabsProps) => {
  const record = useRecordContext<IForm>();

  return (
    <Stack direction="row" alignItems="center" spacing={1}>
      {/* eslint-disable-next-line react/jsx-props-no-spreading */}
      <TabbedShowLayoutTabs {...props} />
      {record && <TestLinkButtons formId={record.id} />}
    </Stack>
  );
};

const FormActiveField = ({ showEdit }: { showEdit: boolean }) => {
  const record = useRecordContext<IForm>();
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const refresh = useRefresh();
  const { wrapperSx, hiddenElementId } = useHiddenUntilHover();
  const [editable, setEditable] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  if (!record) return null;

  const active = { active: !record.inactive };

  const save = async (nextActive: boolean): Promise<void> => {
    setSaving(true);
    try {
      await dataProvider.update(`${resourceNames.FORMS}/${record.id}`, {
        id: 'status',
        data: { inactive: !nextActive },
        previousData: record,
      });
      notify(nextActive ? 'Form activated.' : 'Form inactivated.', { type: 'success' });
      setEditable(false);
      refresh();
    } catch (error) {
      notify(errorNotification(error), { type: 'error' });
    } finally {
      setSaving(false);
      setConfirmOpen(false);
    }
  };

  const submit = (values: FieldValues): void => {
    if (values.active === active.active) {
      setEditable(false);
    } else if (values.active) {
      save(true);
    } else {
      setConfirmOpen(true);
    }
  };

  const field = <BooleanField record={active} source="active" />;

  if (!editable) {
    return (
      <Labeled label="Active" fullWidth sx={wrapperSx}>
        {showEdit ? (
          <Button size="small" onClick={() => setEditable(true)} sx={{ paddingX: 0 }} color="inherit">
            <Box display="flex" gap={2} alignItems="center" justifyContent="space-between" width="100%">
              {field}
              <CreateIcon color="secondary" id={hiddenElementId} fontSize="small" />
            </Box>
          </Button>
        ) : field}
      </Labeled>
    );
  }

  return (
    <Form record={active} onSubmit={submit}>
      <Labeled label="Active" fullWidth>
        <Box display="flex" flexDirection="column" gap={2}>
          <BooleanInput label={false} source="active" helperText={false} />
          <Box display="flex" gap={2} justifyContent="flex-end">
            <Button size="small" variant="outlined" onClick={() => setEditable(false)}>
              Cancel
            </Button>
            <SaveButton icon={null} size="small" variant="contained" disabled={saving} />
          </Box>
        </Box>
      </Labeled>
      <Confirm
        isOpen={confirmOpen}
        loading={saving}
        title="Inactivate form?"
        content="Are you sure? Inactive forms cannot be rendered."
        confirm="Yes, inactivate"
        onConfirm={() => save(false)}
        onClose={() => setConfirmOpen(false)}
      />
    </Form>
  );
};

const RequestPermissionSettings = ({ showEdit }: { showEdit: boolean }) => {
  const record = useRecordContext();
  if (!record?.requestSubmission?.shouldSubmitRequest) return null;

  return (
    <Grid size={12}>
      <EditReferenceField
        source="requestSubmission.process"
        label="Request process"
        showEdit={showEdit}
        reference={resourceNames.PROCESSES}
      />
    </Grid>
  );
};

const FormEditContent = () => {
  const record = useRecordContext<IForm>();
  const dataProvider = useDataProvider();
  const { permissions } = usePermissions<Actions[]>();
  const showEdit = hasAccess(permissions, actions.FORM_UPDATE) && !record?.archived;

  const formId = useGetRecordId();

  const getLogoUrl = useCallback(async () => {
    const { data } = await dataProvider.getOne(`${resourceNames.FORMS}/${formId}`, { id: 'logo' });
    return data.downloadUrl as string;
  }, [dataProvider, formId]);

  return (
    <>
      <FormBreadcrumb />
      <TabbedShowLayout tabs={<FormTabs />}>
        <TabbedShowLayout.Tab label="Information">
          <Labeled label={false} sx={{ display: 'flex' }}>
            <Grid container spacing={6}>
              {/* Form details card */}
              <Grid size={{ xs: 12, lg: 6 }}>
                <Card variant="outlined">
                  <CardHeader title="Form details" />
                  <Divider />
                  <CardContent>
                    <Grid container spacing={2}>
                      <Grid size={12}>
                        <Labeled label="ID">
                          <TextField source="id" />
                        </Labeled>
                      </Grid>
                      <Grid size={12}>
                        <Labeled label="Created at">
                          <DateField source="createdAt" showTime />
                        </Labeled>
                      </Grid>
                      <Grid size={12}>
                        <EditField source="title" label="Title" showEdit={showEdit} />
                      </Grid>
                      <Grid size={12}>
                        <EditImageField
                          source="logo"
                          label="Logo"
                          showEdit={showEdit}
                          getPreviewUrl={getLogoUrl}
                          emptyText="No logo"
                        />
                      </Grid>
                      <Grid size={12}>
                        <Labeled label="Language">
                          <SelectField source="language" choices={languagesChoices} />
                        </Labeled>
                      </Grid>
                      <Grid size={12}>
                        <FormActiveField showEdit={showEdit} />
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>

              {/* Integration configuration card */}
              <Grid size={{ xs: 12, lg: 6 }}>
                <Card variant="outlined">
                  <CardHeader title="Integration configuration" />
                  <Divider />
                  <CardContent>
                    <Grid container spacing={2}>
                      <Grid size={12}>
                        <EditArrayInput
                          source="whitelistedRedirectUrls"
                          label="Whitelisted redirect URLs"
                          showEdit={showEdit}
                        />
                      </Grid>
                      <Grid size={12}>
                        <EditArrayInput
                          source="allowedIframeParentOrigins"
                          label="Allowed iframe parent origins"
                          showEdit={showEdit}
                        />
                      </Grid>
                      <Grid size={12}>
                        <EditField
                          source="requestSubmission.shouldSubmitRequest"
                          label="Should submit request"
                          showEdit={showEdit}
                          type="boolean"
                        />
                      </Grid>
                      <RequestPermissionSettings showEdit={showEdit} />
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>

              {/* Welcome page configuration card */}
              <Grid size={{ xs: 12, lg: 6 }}>
                <Card variant="outlined">
                  <CardHeader title="Welcome page configuration" />
                  <Divider />
                  <CardContent>
                    <Grid container spacing={2}>
                      <Grid size={12}>
                        <EditField source="welcomeTitle" label="Welcome title" showEdit={showEdit} type="textarea" />
                      </Grid>
                      <Grid size={12}>
                        <EditField source="welcomeMessage" label="Welcome message" showEdit={showEdit} type="textarea" />
                      </Grid>
                      <Grid size={12}>
                        <EditField
                          source="skipWelcomePage"
                          label="Skip welcome page"
                          showEdit={showEdit}
                          type="boolean"
                        />
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>

              {/* Thank you page configuration card */}
              <Grid size={{ xs: 12, lg: 6 }}>
                <Card variant="outlined">
                  <CardHeader title="Thank you page configuration" />
                  <Divider />
                  <CardContent>
                    <Grid container spacing={2}>
                      <Grid size={12}>
                        <EditField source="thankYouTitle" label="Thank you title" showEdit={showEdit} type="textarea" />
                      </Grid>
                      <Grid size={12}>
                        <EditField source="thankYouMessage" label="Thank you message" showEdit={showEdit} type="textarea" />
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Labeled>
        </TabbedShowLayout.Tab>
        {hasAccess(permissions, actions.AUDIT_TRACK_LIST) && (
          <TabbedShowLayout.Tab label="Audit trail" path="audit-tracks">
            <Grid container>
              <Grid size={12}>
                <Card variant="outlined">
                  <ReferenceManyField
                    reference="audit-tracks"
                    target="resource.id"
                    filter={{ 'resource.type': resourceTypes.FORM }}
                    sort={{ field: 'date', order: 'DESC' }}
                    pagination={<Pagination />}
                  >
                    <Datagrid sx={boldDataGridStyle} bulkActionButtons={false}>
                      <TextField sortable={false} source="id" label="ID" />
                      <ReferenceMemberField label="User" />
                      <TextField sortable={false} source="description" />
                      <DateField sortable source="date" showTime />
                    </Datagrid>
                  </ReferenceManyField>
                </Card>
              </Grid>
            </Grid>
          </TabbedShowLayout.Tab>
        )}
      </TabbedShowLayout>
    </>
  );
};

const FormEdit = () => {
  const notify = useNotify();

  return (
    <Edit
      mutationMode="pessimistic"
      redirect={false}
      actions={<FormEditActions />}
      mutationOptions={{
        onError: (e: unknown) => notify(errorNotification(e), { type: 'error' }),
      }}
    >
      <FormEditContent />
    </Edit>
  );
};

export default FormEdit;
